/**
 * JobProgressIndicator - List-cell readout for a Work Order or
 * Inspection's lifecycle progress.
 *
 * Renders one of three states:
 *   - Completed   → green check icon (no text, full bar implied).
 *   - Cancelled   → muted "—" so the row reads "no progress to show"
 *                   without looking like an in-flight job stuck at 0%.
 *   - In Progress → 0-100% bar with the status label + percentage.
 *
 * Used by WorkOrdersPage and InspectionsPage as a drop-in replacement
 * for their previous status-pill columns.
 */
import React from 'react';
import { getJobProgress, JobEntityType } from '../../../utils/jobProgress';

export interface JobProgressIndicatorProps {
	/** Lifecycle status slug (work_orders.status / inspections.status). */
	status: string | null | undefined;
	/** Drives the status → percent mapping. */
	type: JobEntityType;
	/** Optional override for the container className. */
	className?: string;
}

export const JobProgressIndicator: React.FC<JobProgressIndicatorProps> = ({ status, type, className = '' }) => {
	const progress = getJobProgress(status, type);

	if (progress.isComplete) {
		// Solid check tile — communicates "done" at a glance without
		// occupying the row width a full progress bar would.
		return (
			<span
				className={['job-progress-indicator job-progress-indicator--complete inline-flex items-center justify-center w-24px h-24px rounded-full bg-green-100 text-green-700 border border-green-200', className].join(' ')}
				data-ref="molecule-job-progress-indicator"
				aria-label="Completed"
				title="Completed"
			>
				<svg className="w-14px h-14px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
					<path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75 10.5 18.75 19.5 5.25" />
				</svg>
			</span>
		);
	}

	if (progress.isCancelled) {
		return (
			<span className={['job-progress-indicator job-progress-indicator--cancelled inline-flex items-center text-12px font-sans text-brand-text-muted', className].join(' ')} data-ref="molecule-job-progress-indicator" title="Cancelled">
				— Cancelled
			</span>
		);
	}

	return (
		<div
			className={['job-progress-indicator flex items-center gap-8px min-w-[140px]', className].join(' ')}
			data-ref="molecule-job-progress-indicator"
			role="progressbar"
			aria-valuenow={progress.percent}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-label={`${progress.label} (${progress.percent}%)`}
		>
			<div className="job-progress-indicator__track flex-1 h-6px rounded-full bg-brand-surface overflow-hidden border border-brand-border">
				<div
					className="job-progress-indicator__bar h-full bg-brand-primary transition-[width] duration-300"
					style={{ width: `${progress.percent}%` }}
				/>
			</div>
			<span className="job-progress-indicator__label text-11px font-sans text-brand-text-muted whitespace-nowrap tabular-nums">
				{progress.label} · {progress.percent}%
			</span>
		</div>
	);
};

export default JobProgressIndicator;
